import { Component } from 'react';
import { connect } from 'react-redux';
import Select from 'react-select-2';
import type { Styles } from 'react-select-2/lib/styles';
import { createStructuredSelector } from 'reselect';

import ProjectsModule from '../../../../redux/projects';
import type { SelectOption } from '../../../../types';
import { selectOptionFactory } from '../../../../utils';
import Icon, { icons } from '../../../generic/Icon';
import type { Reference } from '../../../../../shared/model-typings';

type MapStateProps = {
  branches: Reference[];
  selectedBranch: SelectOption<string>;
  isFetchingBranches: boolean;
};

type DispatchProps = {
  fetchBranches: (locator: string) => void;
};

type ProjectReleaseBranchSelectProps = {
  locator: string;
  isEditable: boolean;
  branch: string;
  onChangeBranch: (branch: string) => void;
} & MapStateProps & DispatchProps;

class ProjectReleaseBranchSelect extends Component<ProjectReleaseBranchSelectProps> {
  /**
   * Fetch this project's branches only if they aren't already loaded.
   */
  async fetchBranchesOnce() {
    const { branches, locator, fetchBranches } = this.props;
    if (branches.length === 0) {
      await fetchBranches(locator);
    }
  }

  get branchOptions() {
    const { branch: selected, branches } = this.props;

    const options = branches.map((branch) => selectOptionFactory(branch.name));

    // if the selected branch hasn't been loaded yet, include it as an option until it shows up
    if (selected && !branches.some((branch) => branch.name === selected)) {
      options.push(selectOptionFactory(selected));
    }

    return options;
  }

  get selectedBranch() {
    const { branch } = this.props;
    return this.branchOptions.find(b => b.value === branch);
  }

  get selectStyles() {
    return {
      control: (styles: Styles) => ({
        ...styles,
        fontSize: '13px',
        whiteSpace: 'nowrap',
      }),
    };
  }

  handleChange = (branch: SelectOption) => {
    const { onChangeBranch } = this.props;
    return onChangeBranch(branch.value);
  };

  render() {
    const { isEditable, isFetchingBranches } = this.props;
    const { selectedBranch } = this;
    const branchName = selectedBranch ? selectedBranch.label : 'master';
    return isEditable ? (
      <Select
        placeholder="master"
        value={selectedBranch}
        options={this.branchOptions}
        onChange={this.handleChange}
        onMenuOpen={() => this.fetchBranchesOnce()}
        styles={this.selectStyles}
        isLoading={isFetchingBranches}
      />
    ) : (
      <div>
        {isFetchingBranches
          ? <Icon icon={icons.circleO} hasSpin />
          : (
            <div className={isEditable ? 'animated fadeInLeft' : ''}>
              {branchName}
            </div>
            )}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  branches: ProjectsModule.selectors.getBranchesForProject,
  isFetchingBranches: ProjectsModule.selectors.getProjectIsFetchingBranches,
});

const mapDispatchToProps = {
  fetchBranches: ProjectsModule.actions.fetchBranches,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProjectReleaseBranchSelect);
